import { Component, h } from '@monygroupcorp/microact';

export class BondingCurve extends Component {
    constructor(props) {
        super(props);
        this.eventBus = props.eventBus;
        this.state = {
            currentPrice: props.price || 0,
            totalBondingSupply: props.contractData?.totalBondingSupply || 0,
            totalMessages: props.contractData?.totalMessages || 0,
            totalNFTs: props.contractData?.totalNFTs || 0,
            dataReady: props.dataReady || false,
            contractEthBalance: props.contractData?.contractEthBalance || 0,
            liquidityPool: props.contractData?.liquidityPool || null,
            chainId: props.chainId || null
        };
        this.canvasRef = null;
    }

    didMount() {
        this.subscribe('contractData:updated', (data) => this.handleContractDataUpdate(data));
        this.subscribe('price:updated', (data) => this.handlePriceUpdate(data));

        this.resizeHandler = () => requestAnimationFrame(() => this.drawCurve());
        window.addEventListener('resize', this.resizeHandler);

        if (this.isLiquidityDeployed()) {
            this.renderDexToolsChart();
        } else {
            this.drawCurve();
        }
    }

    willUnmount() {
        if (this.resizeHandler) {
            window.removeEventListener('resize', this.resizeHandler);
        }
    }

    handleContractDataUpdate(contractData) {
        const newState = {
            totalBondingSupply: contractData.totalBondingSupply,
            totalMessages: contractData.totalMessages,
            totalNFTs: contractData.totalNFTs,
            contractEthBalance: contractData.contractEthBalance,
            dataReady: true
        };

        const oldLiquidityPool = this.state.liquidityPool;
        if (oldLiquidityPool !== contractData.liquidityPool) {
            newState.liquidityPool = contractData.liquidityPool;
        }

        this.setState(newState);

        if (this.isLiquidityDeployed()) {
            this.renderDexToolsChart();
        } else {
            this.drawCurve();
        }
    }

    handlePriceUpdate(data) {
        this.setState({ currentPrice: data.price });
        if (!this.isLiquidityDeployed()) {
            this.drawCurve();
        }
    }

    isLiquidityDeployed() {
        const { liquidityPool } = this.state;
        return liquidityPool && liquidityPool !== '0x0000000000000000000000000000000000000000';
    }

    renderDexToolsChart() {
        if (!this.containerRef) return;

        try {
            const networkName = this.getNetworkName(this.state.chainId);
            const chartUrl = `https://www.dextools.io/widget-chart/en/${networkName}/pe-light/${this.state.liquidityPool}?theme=dark&chartType=2&chartResolution=30&drawingToolbars=false`;

            // Clear and insert iframe
            this.containerRef.innerHTML = '';
            const iframe = document.createElement('iframe');
            iframe.id = 'dextools-widget';
            iframe.title = '$EXEC DEXTools Trading Chart';
            iframe.width = '100%';
            iframe.height = '100%';
            iframe.src = chartUrl;
            iframe.style.border = 'none';
            iframe.onerror = () => {
                this.containerRef.innerHTML = '<div style="display: flex; align-items: center; justify-content: center; height: 100%; color: #888;"><div>Chart unavailable</div></div>';
            };
            this.containerRef.appendChild(iframe);
        } catch (error) {
            console.error('Error rendering DEXTools chart:', error);
        }
    }

    getNetworkName(chainId) {
        const networks = {
            1: 'ether',
            5: 'goerli',
        };
        return networks[chainId] || 'ether';
    }

    drawCurve() {
        if (!this.canvasRef) return;

        const canvas = this.canvasRef;
        const ctx = canvas.getContext('2d');

        canvas.width = canvas.offsetWidth;
        canvas.height = canvas.offsetHeight;

        ctx.clearRect(0, 0, canvas.width, canvas.height);
        this.drawBaseCurve(ctx, canvas);

        if (this.state.dataReady) {
            this.drawDataElements(ctx, canvas);
        } else {
            this.drawPlaceholderLabels(ctx);
        }
    }

    drawBaseCurve(ctx, canvas) {
        const curveColor = getComputedStyle(document.documentElement).getPropertyValue('--bonding-curve-color').trim() || '#764ba2';
        ctx.strokeStyle = curveColor;
        ctx.lineWidth = 2;

        const curve = (x) => Math.pow(0.1 + (x * 0.8), 3.5);
        const points = this.calculateCurvePoints(canvas, curve);
        this.drawCurvePath(ctx, points);
    }

    drawDataElements(ctx, canvas) {
        const curve = (x) => Math.pow(0.1 + (x * 0.8), 3.5);
        const maxPrice = 0.08;
        const currentPosition = Math.max(0.1, Math.min(0.9, Math.pow(this.state.currentPrice / maxPrice, 1 / 3.5)));
        const points = this.calculateCurvePoints(canvas, curve);
        this.drawCurrentPosition(ctx, points, currentPosition);
        this.drawLabels(ctx);
    }

    drawPlaceholderLabels(ctx) {
        ctx.fillStyle = '#666666';
        ctx.font = '12px Courier New';
        ctx.fillText('Loading price data...', 10, 20);
        ctx.fillText('Loading supply data...', 10, 40);
        ctx.fillText('Loading NFT data...', 10, 60);
    }

    calculateCurvePoints(canvas, curve) {
        const points = [];
        const numPoints = 100;
        for (let i = 0; i <= numPoints; i++) {
            const x = i / numPoints;
            const y = curve(x);
            const canvasX = x * canvas.width * 0.8 + canvas.width * 0.1;
            const canvasY = canvas.height * 0.9 - y * canvas.height * 0.8;
            points.push({ x: canvasX, y: canvasY });
        }
        return points;
    }

    drawCurvePath(ctx, points) {
        ctx.beginPath();
        ctx.moveTo(points[0].x, points[0].y);
        for (let i = 1; i < points.length; i++) {
            ctx.lineTo(points[i].x, points[i].y);
        }
        ctx.stroke();
    }

    drawCurrentPosition(ctx, points, currentPosition) {
        const segmentSize = 0.05;
        const startIndex = Math.floor((currentPosition - segmentSize / 2) * points.length);
        const endIndex = Math.floor((currentPosition + segmentSize / 2) * points.length);

        if (startIndex >= 0 && endIndex < points.length) {
            const curveColor = getComputedStyle(document.documentElement).getPropertyValue('--bonding-curve-color').trim() || '#764ba2';
            ctx.beginPath();
            ctx.strokeStyle = curveColor;
            ctx.lineWidth = 4;

            ctx.moveTo(points[startIndex].x, points[startIndex].y);
            for (let i = startIndex; i <= endIndex; i++) {
                ctx.lineTo(points[i].x, points[i].y);
            }
            ctx.stroke();

            const centerIndex = Math.floor((startIndex + endIndex) / 2);
            const centerPoint = points[centerIndex];

            ctx.beginPath();
            ctx.arc(centerPoint.x, centerPoint.y, 4, 0, Math.PI * 2);
            ctx.fillStyle = '#FF0000';
            ctx.fill();
        }
    }

    drawLabels(ctx) {
        ctx.fillStyle = '#666666';
        ctx.font = '12px Courier New';
        ctx.fillText(`${this.state.currentPrice.toFixed(4)} ETH / EXEC`, 10, 20);
        ctx.fillText(`Total Supply: ${this.state.totalBondingSupply.toLocaleString()} EXEC`, 10, 40);
        ctx.fillText(`Total Messages: ${this.state.totalMessages.toLocaleString()}`, 10, 60);
        ctx.fillText(`Total NFTs: ${this.state.totalNFTs.toLocaleString()}`, 10, 80);
        ctx.fillText(`Contract ETH Balance: ${this.state.contractEthBalance}`, 10, 100);
    }

    render() {
        const isLiquidityDeployed = this.isLiquidityDeployed();

        return h('div', {
            className: 'bonding-curve marble-bg',
            ref: el => this.containerRef = el
        },
            !isLiquidityDeployed && h('canvas', {
                id: 'curveChart',
                ref: el => {
                    this.canvasRef = el;
                    if (el) this.drawCurve();
                }
            })
        );
    }

    static get styles() {
        return `
            .bonding-curve {
                width: 100%;
                height: 90%;
                min-height: 400px;
                background: #1a1a1a;
                border-radius: 8px;
                padding: 0px;
                overflow: hidden;
                position: relative;
            }

            #curveChart {
                width: 100%;
                height: 100%;
            }

            #dextools-widget {
                width: 100%;
                height: 100%;
                position: absolute;
                top: 0;
                left: 0;
                border: none;
            }
        `;
    }
}

export default BondingCurve;
